import { NeutralColor, PrimaryColor } from "node-library/dist/colors";
import { useEffect, useRef, useState } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { RightArrowIcon } from "./svgIcons";
import {
	Button,
	IconButton,
	Menu,
	MenuItem,
	Popover,
	SxProps,
	Theme,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";

interface ButtonProps {
	id: string;
	buttonClassName?: string;
	children?: any;
	type?: "submit" | "reset" | "button" | undefined;
	size?: "flex" | "large" | "medium" | "small" | "xsmall";
	disabled?: boolean;
	onClick: (e: any) => void;
	ref?: React.Ref<HTMLButtonElement>;
}

function SkyBlueButton(props: ButtonProps): JSX.Element {
	const size = props.size || "medium";
	return (
		<div className="button-wrapper sky-blue-button-wrapper">
			<button
				onClick={props.onClick}
				id={props.id}
				type={props.type}
				className={`common-button common-button-${size} common-sky-blue-button typography-body-01 ${
					props.buttonClassName || ""
				}`}
				disabled={props.disabled}
			>
				{props.children}
			</button>
		</div>
	);
}

function BlueButton(props: ButtonProps): JSX.Element {
	const size = props.size || "medium";

	return (
		<div className="button-wrapper blue-button-wrapper">
			<button
				id={props.id}
				onClick={props.onClick}
				type={props.type}
				className={`common-button common-button-${size} common-blue-button typography-body-01 ${
					props.buttonClassName || ""
				}`}
				disabled={props.disabled}
				ref={props.ref}
			>
				{props.children}
			</button>
		</div>
	);
}

function WhiteButton(props: ButtonProps): JSX.Element {
	const size = props.size || "medium";

	return (
		<div className="button-wrapper white-button-wrapper">
			<button
				id={props.id}
				onClick={props.onClick}
				type={props.type}
				className={`common-button common-button-${size} common-white-button typography-body-01 ${
					props.buttonClassName || ""
				}`}
				disabled={props.disabled}
			>
				{props.children}
			</button>
		</div>
	);
}

function TextButton(props: ButtonProps): JSX.Element {
	const size = props.size || "medium";

	return (
		<div className="button-wrapper text-button-wrapper">
			<button
				id={props.id}
				onClick={props.onClick}
				type={props.type}
				className={`common-button common-button-${size} common-text-button typography-body-01 typography-body-bold ${
					props.buttonClassName || ""
				}`}
				disabled={props.disabled}
			>
				{props.children}
			</button>
		</div>
	);
}

interface ArrowButtonProps extends ButtonProps {
	arrowButtonColor: "blue" | "sky-blue" | "white";
}

function ArrowButton(props: ArrowButtonProps): JSX.Element {
	const size = props.size || "medium";
	const arrowButtonClassName: string = "button-with-arrow";

	let button: React.ReactNode;
	switch (props.arrowButtonColor) {
		case "blue":
			button = (
				<BlueButton
					id={props.id}
					type={props.type}
					size={props.size}
					disabled={props.disabled}
					buttonClassName={
						props.buttonClassName +
						` ${arrowButtonClassName}-${size}` +
						` blue-${arrowButtonClassName}`
					}
					onClick={props.onClick}
				>
					{<RightArrowIcon strokeColor="white" />}
				</BlueButton>
			);
			break;
		case "sky-blue":
			button = (
				<SkyBlueButton
					id={props.id}
					type={props.type}
					size={props.size}
					disabled={props.disabled}
					buttonClassName={
						props.buttonClassName +
						` ${arrowButtonClassName}-${size}` +
						` sky-blue-${arrowButtonClassName}`
					}
					onClick={props.onClick}
				>
					{
						<RightArrowIcon
							strokeColor={props.disabled ? "white" : PrimaryColor.PRIMARY_800}
						/>
					}
				</SkyBlueButton>
			);
			break;
		case "white":
			button = (
				<WhiteButton
					id={props.id}
					type={props.type}
					size={props.size}
					disabled={props.disabled}
					buttonClassName={
						props.buttonClassName +
						` ${arrowButtonClassName}-${size}` +
						` white-${arrowButtonClassName}`
					}
					onClick={props.onClick}
				>
					{
						<RightArrowIcon
							strokeColor={
								props.disabled
									? NeutralColor.NEUTRAL_GREY_100
									: NeutralColor.NEUTRAL_GREY_900
							}
						/>
					}
				</WhiteButton>
			);
			break;
	}
	return button;
}

interface OutlinedButtonProps extends ButtonProps {
	outlineColor: string;
}

function OutlinedButton(props: OutlinedButtonProps): JSX.Element {
	const size = props.size || "medium";

	return (
		<div className="button-wrapper outlined-button-wrapper">
			<button
				id={props.id}
				onClick={props.onClick}
				type={props.type}
				className={`common-button common-button-${size} common-outlined-button typography-body-02 ${
					props.buttonClassName || ""
				}`}
				style={{ borderColor: props.outlineColor, color: props.outlineColor }}
				disabled={props.disabled}
			>
				{props.children}
			</button>
		</div>
	);
}

interface DropdownButtonProps extends ButtonProps {
	buttonColor?: "blue" | "sky blue" | "white";
	className?: string;
	menuOptions: Array<{
		key: string;
		onClick: () => void;
		children: any;
		widthToShow?: number;
		hasSubmenu?: boolean; // Flag to determine if this item has a submenu
		submenuOptions?: Array<{
			key: string;
			children: React.ReactNode;
			onClick: () => void;
		}>; // Submenu items
	}>;
	container?: HTMLElement | null;
	isFlexButton?: boolean;
}

function DropdownButton(props: DropdownButtonProps): JSX.Element {
	const [anchorEl, setAnchorEl] = useState(null);
	const [isOpen, setIsOpen] = useState(false);
	const [containerWidth, setContainerWidth] = useState<number>(0);
	const [buttonWidth, setButtonWidth] = useState<number>(0);

	const rightIconClassName: string = "button-with-right-icon";

	const handleClick = (event: any) => {
		setAnchorEl(event.currentTarget);
		setIsOpen(!isOpen); // Toggle menu open/close
		if (props.container) {
			const width = props.container.offsetWidth;
			setContainerWidth(width);
		}
		if (props.isFlexButton) {
			setButtonWidth(event.currentTarget.offsetWidth);
		}
	};

	const handleClose = () => {
		setAnchorEl(null);
		setIsOpen(false); // Close menu
	};

	let button: React.ReactNode;
	switch (props.buttonColor) {
		case "blue":
			button = (
				<BlueButton
					id={props.id}
					type={props.type}
					size={props.size}
					disabled={props.disabled}
					buttonClassName={props.buttonClassName + ` ${rightIconClassName}`}
					onClick={(e) => {
						handleClick(e);
					}} // Open dropdown on button click
				>
					{props.children}
					{isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
				</BlueButton>
			);
			break;
		case "sky blue":
			button = (
				<SkyBlueButton
					id={props.id}
					type={props.type}
					size={props.size}
					disabled={props.disabled}
					buttonClassName={props.buttonClassName + ` ${rightIconClassName}`}
					onClick={(e) => {
						handleClick(e);
					}} // Open dropdown on button click
				>
					{props.children}
					{isOpen ? (
						<KeyboardArrowUpIcon sx={{ color: "white" }} />
					) : (
						<KeyboardArrowDownIcon sx={{ color: "white" }} />
					)}
				</SkyBlueButton>
			);
			break;
		case "white":
			button = (
				<WhiteButton
					id={props.id}
					type={props.type}
					size={props.size}
					disabled={props.disabled}
					buttonClassName={props.buttonClassName + ` ${rightIconClassName}`}
					onClick={(e) => {
						handleClick(e);
					}} // Open dropdown on button click
				>
					{props.children}
					{isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
				</WhiteButton>
			);
			break;
	}

	return (
		<div className={props.className}>
			{button}
			<Menu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)} // Open menu if anchorEl is set
				onClose={handleClose} // Close menu when an item is clicked or clicked outside
				sx={{ marginTop: "5px" }}
			>
				{props.menuOptions.map((option) => {
					return (
						<MenuItem
							sx={
								option.widthToShow
									? {
											display:
												option.widthToShow > containerWidth ? "flex" : "none",
									  }
									: props.isFlexButton
									? {
											width: props.isFlexButton ? buttonWidth : "unset",
											minWidth: "max-content",
									  }
									: {}
							}
							key={option.key}
							onClick={() => {
								option.onClick();
								handleClose();
							}}
						>
							{option.children}
						</MenuItem>
					);
				})}
			</Menu>
		</div>
	);
}

function FlexDropdownButton(props: DropdownButtonProps): JSX.Element {
	const [anchorEl, setAnchorEl] = useState(null);
	const [isOpen, setIsOpen] = useState(false);
	const [containerWidth, setContainerWidth] = useState<number>(0);
	const [buttonWidth, setButtonWidth] = useState<number>(0);
	const buttonRef = useRef<HTMLButtonElement | null>(null);

	const rightIconClassName: string = "button-with-right-icon";

	const handleClick = (event: any) => {
		setAnchorEl(event.currentTarget);
		setIsOpen(!isOpen); // Toggle menu open/close
		if (props.container) {
			const width = props.container.offsetWidth;
			setContainerWidth(width);
		}
		// if (props.isFlexButton) {
		// 	setButtonWidth(event.currentTarget.offsetWidth);
		// }
	};

	const handleClose = () => {
		setAnchorEl(null);
		setIsOpen(false); // Close menu
	};

	useEffect(() => {
		if (!buttonRef.current) return;
		const resizeObserver = new ResizeObserver((entries) => {
			for (let entry of entries) {
				const rect = entry.target.getBoundingClientRect();
				setButtonWidth(rect.width); // updates width dynamically
			}
		});

		resizeObserver.observe(buttonRef.current);

		return () => resizeObserver.disconnect();
	}, [isOpen]);

	return (
		<div className={props.className}>
			{
				<button
					id={props.id}
					onClick={(e) => handleClick(e)}
					type={props.type}
					className={`common-button common-button-${
						props.size || "medium"
					} common-blue-button typography-body-01 ${
						props.buttonClassName || ""
					} ${rightIconClassName}`}
					disabled={props.disabled}
					ref={buttonRef}
				>
					{props.children}
					{isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
				</button>
			}
			<Menu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)} // Open menu if anchorEl is set
				onClose={handleClose} // Close menu when an item is clicked or clicked outside
				slotProps={{
					paper: {
						sx: {
							marginTop: "4px",
							border: "solid 1px #2E64C9",
							boxShadow: "0px 0px 8px #2945A940",
						},
					},
				}}
			>
				{props.menuOptions.map((option) => {
					return (
						<MenuItem
							sx={
								option.widthToShow
									? {
											display:
												option.widthToShow > containerWidth ? "flex" : "none",
									  }
									: props.isFlexButton
									? {
											width: props.isFlexButton ? buttonWidth : "unset",
											minWidth: "max-content",
									  }
									: {}
							}
							key={option.key}
							onClick={() => {
								option.onClick();
								handleClose();
							}}
						>
							{option.children}
						</MenuItem>
					);
				})}
			</Menu>
		</div>
	);
}

function KebabDropdownButton(props: DropdownButtonProps): JSX.Element {
	const [anchorEl, setAnchorEl] = useState(null);
	const [submenuAnchorEl, setSubmenuAnchorEl] = useState<null | HTMLElement>(
		null
	); // For submenu

	const [isOpen, setIsOpen] = useState(false);

	const handleClick = (event: any) => {
		event.stopPropagation();
		event.preventDefault();
		setAnchorEl(event.currentTarget);
		setIsOpen(!isOpen); // Toggle menu open/close
	};

	const handleClose = (event: any) => {
		event.stopPropagation();
		setAnchorEl(null);
		setSubmenuAnchorEl(null); // Close submenu
		setIsOpen(false); // Close menu
	};
	const handleSubmenuOpen = (event: any) => {
		setSubmenuAnchorEl(event.currentTarget); // Open submenu for specific item
	};

	const handleSubmenuClose = () => {
		setSubmenuAnchorEl(null); // Close submenu
	};
	return (
		<div>
			<IconButton id={props.id} disabled={props.disabled} onClick={handleClick}>
				<MoreVertIcon /> {/* Kebab (More) icon */}
			</IconButton>

			<Menu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)} // Open menu if anchorEl is set
				onClose={handleClose} // Close menu when an item is clicked or clicked outside
			>
				{props.menuOptions.map((option) => (
					<MenuItem
						key={option.key}
						onClick={(e) => {
							e.stopPropagation();
							option.onClick();
							if (!option.hasSubmenu) {
								handleClose(e);
							}
						}}
						onMouseEnter={option.hasSubmenu ? handleSubmenuOpen : undefined} // Open submenu on hover
						onMouseLeave={option.hasSubmenu ? handleSubmenuClose : undefined} // Open submenu on hover
					>
						{option.children}

						{/* Submenu */}
						{option.hasSubmenu && option.submenuOptions && (
							<Menu
								anchorEl={submenuAnchorEl} // Set submenu anchorEl
								open={Boolean(submenuAnchorEl)} // Open submenu if anchorEl is set
								onClose={handleSubmenuClose} // Close submenu
								anchorOrigin={{
									vertical: "top",
									horizontal: "right",
								}}
								transformOrigin={{
									vertical: "top",
									horizontal: 5,
								}}
								style={{ pointerEvents: "none" }}
								hideBackdrop
							>
								{option.submenuOptions.map((submenuOption) => (
									<MenuItem
										key={submenuOption.key}
										onClick={(e) => {
											e.stopPropagation();
											submenuOption.onClick();
											handleSubmenuClose();
											handleClose(e);
										}}
										style={{ pointerEvents: "auto" }}
									>
										{submenuOption.children}
									</MenuItem>
								))}
							</Menu>
						)}
					</MenuItem>
				))}
			</Menu>
		</div>
	);
}

interface CustomIconButtonProps {
	id: string;
	buttonClassName?: string;
	icon: any;
	type?: "submit" | "reset" | "button" | undefined;
	size?: "medium" | "small";
	disabled?: boolean;
	hoverColor?: string;
	onClick: (e: any) => void;
}

function CustomIconButton(props: CustomIconButtonProps): JSX.Element {
	const size = props.size || "medium";
	let width = 44;
	let height = 44;
	let padding = "0px 0px";
	if (size == "small") {
		width = 32;
		height = 32;
		padding = "6px 8px";
	}

	return (
		<Button
			id={props.id}
			onClick={props.onClick}
			type={props.type}
			className={`icon-button icon-button-${size} ${
				props.buttonClassName || ""
			}`}
			sx={{
				minWidth: "auto", // Remove the default min-width
				width: width,
				height: height,
				padding: padding,
				".MuiSvgIcon-root": {
					color: NeutralColor.NEUTRAL_GREY_900, // Change icon color when the button is pressed
				},

				"&.Mui-disabled .MuiSvgIcon-root": {
					color: NeutralColor.NEUTRAL_GREY_300, // Change icon color when button is disabled
				},
				"&:focus": {
					color: "transparent", // Make sure focus color is transparent
					outline: "none", // Remove the focus outline (border)
					border: "none ", // Remove the default border on focus
				},

				"&:active": {
					outline: "none", // Make sure no border shows when the button is active (clicked)
				},
				"&:hover": {
					backgroundColor: props.hoverColor,
				},
			}}
			disabled={props.disabled}
		>
			{props.icon}
		</Button>
	);
}
export {
	SkyBlueButton,
	BlueButton,
	WhiteButton,
	OutlinedButton,
	DropdownButton,
	KebabDropdownButton,
	TextButton,
	CustomIconButton,
	ArrowButton,
	FlexDropdownButton,
};
